const DEFAULT_CHUNK_SIZE_BYTES = 16 * 1024 * 1024;
const DEFAULT_RANGE_RETRY_LIMIT = 3;
const DEFAULT_FULL_RETRY_LIMIT = 1;

type DownloadProgress = {
  text: string;
  percent: number;
};

export type VideoChunkSnapshot = {
  blob: Blob;
  loadedBytes: number;
  totalBytes: number | null;
  complete: boolean;
};

type DownloadOptions = {
  signal: AbortSignal;
  onProgress: (progress: DownloadProgress) => void;
  onChunkComplete?: (snapshot: VideoChunkSnapshot) => void;
  chunkSizeBytes?: number;
  rangeRetryLimit?: number;
  fullRetryLimit?: number;
};

type ParsedContentRange = {
  start: number;
  end: number;
  total: number | null;
};

type ReadResult = {
  bytesRead: number;
  failedAfterBytes: boolean;
};

type RangeReadResult = ReadResult & {
  loadedBytes: number;
};

function createAbortError(): Error {
  const error = new Error('Video download aborted');
  error.name = 'AbortError';
  return error;
}

function assertNotAborted(signal: AbortSignal): void {
  if (signal.aborted) throw createAbortError();
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

function parsePositiveInteger(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function responseStatus(response: Response): number {
  return response.status || (response.ok ? 200 : 0);
}

export function parseContentRange(value: string | null): ParsedContentRange | null {
  if (!value) return null;
  const match = /^bytes\s+(\d+)-(\d+)\/(\d+|\*)$/i.exec(value.trim());
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2]);
  const total = match[3] === '*' ? null : Number(match[3]);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  if (total !== null && (!Number.isFinite(total) || total <= end)) return null;
  return { start, end, total };
}

function progressPercent(loadedBytes: number, totalBytes: number | null): number {
  if (!totalBytes || totalBytes <= 0) return 0;
  return Math.min(99, Math.max(0, Math.round((loadedBytes / totalBytes) * 100)));
}

function emitDownloadProgress(
  onProgress: (progress: DownloadProgress) => void,
  loadedBytes: number,
  totalBytes: number | null,
  retryText?: string
): void {
  onProgress({
    text: retryText ? `Downloading video... ${retryText}` : 'Downloading video...',
    percent: progressPercent(loadedBytes, totalBytes),
  });
}

function emitChunkComplete(
  options: DownloadOptions,
  parts: BlobPart[],
  loadedBytes: number,
  totalBytes: number | null,
  contentType: string,
  complete: boolean
): void {
  options.onChunkComplete?.({
    blob: new Blob(parts, { type: contentType }),
    loadedBytes,
    totalBytes,
    complete,
  });
}

async function cancelReader(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<void> {
  try {
    await reader.cancel();
  } catch {
    // ignore stream cancellation failures
  }
}

async function readResponseBody(
  response: Response,
  signal: AbortSignal,
  onBytes: (value: Uint8Array) => void
): Promise<ReadResult> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Failed to read video stream');

  let bytesRead = 0;
  try {
    while (true) {
      assertNotAborted(signal);
      const { done, value } = await reader.read();
      assertNotAborted(signal);
      if (done) break;
      if (value && value.byteLength > 0) {
        bytesRead += value.byteLength;
        onBytes(value);
      }
    }
    return { bytesRead, failedAfterBytes: false };
  } catch (error) {
    if (signal.aborted || isAbortError(error)) {
      await cancelReader(reader);
      throw createAbortError();
    }
    return { bytesRead, failedAfterBytes: bytesRead > 0 };
  } finally {
    (reader as { releaseLock?: () => void }).releaseLock?.();
  }
}

function copyBlobPart(value: Uint8Array): BlobPart {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy.buffer;
}

function shouldRetry(started: boolean, usedRetries: number, retryLimit: number): boolean {
  return started && usedRetries < retryLimit;
}

async function fetchVideo(
  videoUrl: string,
  signal: AbortSignal,
  headers?: HeadersInit
): Promise<Response> {
  assertNotAborted(signal);
  const init: RequestInit = { mode: 'cors', signal };
  if (headers) init.headers = headers;
  return fetch(videoUrl, init);
}

async function downloadFullVideo(
  videoUrl: string,
  initialResponse: Response | null,
  options: Pick<DownloadOptions, 'signal' | 'onProgress' | 'onChunkComplete'>,
  fullRetryLimit: number
): Promise<Blob> {
  const { signal, onProgress } = options;
  let retryCount = 0;
  let response = initialResponse;

  while (true) {
    const parts: BlobPart[] = [];
    let loadedBytes = 0;
    let totalBytes: number | null = null;
    let contentType = 'video/mp4';

    try {
      response ??= await fetchVideo(videoUrl, signal);
      assertNotAborted(signal);
      const status = responseStatus(response);
      if (!response.ok) throw new Error(`Video fetch failed (${status})`);

      totalBytes = parsePositiveInteger(response.headers.get('content-length'));
      contentType = response.headers.get('content-type') ?? contentType;
      emitDownloadProgress(onProgress, loadedBytes, totalBytes);

      const result = await readResponseBody(response, signal, (value) => {
        parts.push(copyBlobPart(value));
        loadedBytes += value.byteLength;
        emitDownloadProgress(onProgress, loadedBytes, totalBytes);
      });

      if (result.failedAfterBytes) {
        throw new Error('Video stream interrupted');
      }

      onProgress({ text: 'Downloading video...', percent: 100 });
      const blob = new Blob(parts, { type: contentType });
      options.onChunkComplete?.({
        blob,
        loadedBytes,
        totalBytes,
        complete: true,
      });
      return blob;
    } catch (error) {
      if (signal.aborted || isAbortError(error)) throw error;
      const started = loadedBytes > 0;
      if (!shouldRetry(started, retryCount, fullRetryLimit)) throw error;
      retryCount += 1;
      response = null;
      emitDownloadProgress(onProgress, 0, totalBytes, `retry ${retryCount}/${fullRetryLimit}`);
    }
  }
}

async function readRangeResponse(
  response: Response,
  parts: BlobPart[],
  loadedBytes: number,
  totalBytes: number | null,
  options: Required<Pick<DownloadOptions, 'signal' | 'onProgress'>>
): Promise<RangeReadResult> {
  const { signal, onProgress } = options;
  let nextLoadedBytes = loadedBytes;
  const result = await readResponseBody(response, signal, (value) => {
    parts.push(copyBlobPart(value));
    nextLoadedBytes += value.byteLength;
    emitDownloadProgress(onProgress, nextLoadedBytes, totalBytes);
  });

  return {
    ...result,
    loadedBytes: nextLoadedBytes,
  };
}

export async function downloadVideoWithResume(
  videoUrl: string,
  options: DownloadOptions
): Promise<Blob> {
  const signal = options.signal;
  const onProgress = options.onProgress;
  const chunkSizeBytes = options.chunkSizeBytes ?? DEFAULT_CHUNK_SIZE_BYTES;
  const rangeRetryLimit = options.rangeRetryLimit ?? DEFAULT_RANGE_RETRY_LIMIT;
  const fullRetryLimit = options.fullRetryLimit ?? DEFAULT_FULL_RETRY_LIMIT;

  const parts: BlobPart[] = [];
  let loadedBytes = 0;
  let totalBytes: number | null = null;
  let retryCount = 0;
  let contentType = 'video/mp4';

  emitDownloadProgress(onProgress, loadedBytes, totalBytes);

  while (true) {
    assertNotAborted(signal);
    const rangeEnd =
      totalBytes === null
        ? loadedBytes + chunkSizeBytes - 1
        : Math.min(totalBytes - 1, loadedBytes + chunkSizeBytes - 1);
    const headers = { Range: `bytes=${loadedBytes}-${rangeEnd}` };

    let response: Response;
    try {
      response = await fetchVideo(videoUrl, signal, headers);
    } catch (error) {
      if (signal.aborted || isAbortError(error)) throw error;
      if (!shouldRetry(loadedBytes > 0, retryCount, rangeRetryLimit)) throw error;
      retryCount += 1;
      emitDownloadProgress(
        onProgress,
        loadedBytes,
        totalBytes,
        `retry ${retryCount}/${rangeRetryLimit}`
      );
      continue;
    }

    const status = responseStatus(response);
    if (status === 200 && loadedBytes === 0) {
      return downloadFullVideo(videoUrl, response, options, fullRetryLimit);
    }

    if (status === 416 && loadedBytes > 0) {
      onProgress({ text: 'Downloading video...', percent: 100 });
      emitChunkComplete(options, parts, loadedBytes, totalBytes, contentType, true);
      return new Blob(parts, { type: contentType });
    }

    if (status !== 206) {
      if (!shouldRetry(loadedBytes > 0, retryCount, rangeRetryLimit)) {
        throw new Error(`Video range fetch failed (${status})`);
      }
      retryCount += 1;
      emitDownloadProgress(
        onProgress,
        loadedBytes,
        totalBytes,
        `retry ${retryCount}/${rangeRetryLimit}`
      );
      continue;
    }

    contentType = response.headers.get('content-type') ?? contentType;
    const contentRange = parseContentRange(response.headers.get('content-range'));
    if (contentRange) {
      if (contentRange.start !== loadedBytes) {
        throw new Error(
          `Unexpected video range ${contentRange.start}-${contentRange.end}; expected ${loadedBytes}`
        );
      }
      totalBytes = contentRange.total;
    }

    const beforeChunkBytes = loadedBytes;
    try {
      const result = await readRangeResponse(response, parts, loadedBytes, totalBytes, {
        signal,
        onProgress,
      });
      loadedBytes = result.loadedBytes;
      const chunkBytes = result.bytesRead;

      if (result.failedAfterBytes) {
        throw new Error('Video stream interrupted');
      }

      if (totalBytes !== null && loadedBytes >= totalBytes) {
        onProgress({ text: 'Downloading video...', percent: 100 });
        emitChunkComplete(options, parts, loadedBytes, totalBytes, contentType, true);
        return new Blob(parts, { type: contentType });
      }

      if (totalBytes === null && chunkBytes > 0 && chunkBytes < chunkSizeBytes) {
        onProgress({ text: 'Downloading video...', percent: 100 });
        emitChunkComplete(options, parts, loadedBytes, totalBytes, contentType, true);
        return new Blob(parts, { type: contentType });
      }

      if (loadedBytes === beforeChunkBytes) {
        throw new Error('Video range response contained no bytes');
      }

      emitChunkComplete(options, parts, loadedBytes, totalBytes, contentType, false);
      // A chunk landed: reset the retry budget so it is per-chunk, not shared
      // across the whole download. Otherwise a handful of transient network
      // blips spread over a long video exhaust the 3-retry budget and abort
      // the rest, leaving the episode loaded only partway.
      retryCount = 0;
    } catch (error) {
      if (signal.aborted || isAbortError(error)) throw error;
      if (!shouldRetry(loadedBytes > 0, retryCount, rangeRetryLimit)) throw error;
      retryCount += 1;
      emitDownloadProgress(
        onProgress,
        loadedBytes,
        totalBytes,
        `retry ${retryCount}/${rangeRetryLimit}`
      );
    }
  }
}
